<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="form-grid m-t-20 m-l-20">

        <!-- From Date -->
        <div class="form-group">
          <label>From Date</label>
          <input type="date" v-model="fromDate" class="search-input" />
        </div>

        <!-- To Date -->
        <div class="form-group">
          <label>To Date</label>
          <input type="date" v-model="toDate" class="search-input" />
        </div>

        <!-- Distributor Dropdown -->
        <div class="form-group">
          <label>Distributor</label>
          <select v-model="selectedDistributor" class="search-input sales-rep-select" :disabled="isDistributorLocked">
            <option value="">Select Distributor</option>
            <option v-if="!isDistributorUser" :value="ALL">All Distributors</option>
            <option
              v-for="distributor in distributorsList"
              :key="distributor.profile_id"
              :value="String(distributor.profile_id)"
            >
              {{ distributor.name }}
            </option>
          </select>
        </div>

        <!-- Search Button -->
        <div class="form-group m-t-25">
          <button @click="performSearch" class="btn button-default">Search</button>
        </div>

        <!-- Create Schedule Button -->
        <div class="form-group new-dispatch-container m-t-25">
          <button type="button" class="btn button-default" @click="navigateToCreateSchedule">
            Create Schedule
          </button>
        </div>

      </div>

      <!-- Loading Spinner -->
      <div v-if="loading" class="loading-spinner mt-3">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <!-- Schedule Table -->
      <div class="mt-4">
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr class="border-bottom-primary">
                <th scope="col">#</th>
                <th scope="col">Date</th>
                <th scope="col">Delivery Window</th>
                <th scope="col">Routes</th>
                <th scope="col">Dealers</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="scheduleList.length === 0 && !loading">
                <td colspan="5" class="text-center">No schedules found</td>
              </tr>
              <tr v-for="(item, index) in scheduleList" :key="item.id">
                <td>{{ index + 1 }}</td>
                <td>{{ item.date_schedule }}</td>
                <td>
                  <span class="window-badge">{{ item.delivery_window || '—' }}</span>
                </td>
                <td>
                  <div class="tag-list">
                    <span
                      class="tag tag-route"
                      v-for="route in item.routes"
                      :key="route"
                    >
                      <i class="fa fa-map-marker"></i> {{ route }}
                    </span>
                    <span v-if="!item.routes || item.routes.length === 0" class="no-data">—</span>
                  </div>
                </td>
                <td>
                  <div class="tag-list">
                    <span
                      class="tag tag-dealer"
                      v-for="dealer in item.dealers"
                      :key="dealer"
                    >
                      <i class="fa fa-user"></i> {{ dealer }}
                    </span>
                    <span v-if="!item.dealers || item.dealers.length === 0" class="no-data">—</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { fetchDistributors, fetchDeliveryScheduleList } from '@/services/dataService';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

export default {
  name: 'DeliveryScheduleList',

  setup() {
    const router = useRouter();
    const toast  = useToast();

    const navigateToCreateSchedule = () => {
      router.push('/dispatch-locations');
    };

    return { router, toast, navigateToCreateSchedule };
  },

  data() {
    const today = new Date().toISOString().split('T')[0];

    // Default fromDate = first day of current month
    const firstOfMonth = new Date();
    firstOfMonth.setDate(1);
    const fromDefault = firstOfMonth.toISOString().split('T')[0];

    const ALL = '__all__';

    return {
      userRole:            localStorage.getItem('user_label'),
      token:               localStorage.getItem('token_set'),
      profileId:           localStorage.getItem('profile_id'),

      fromDate:            fromDefault,
      toDate:              today,

      selectedDistributor: '',
      distributorsList:    [],
      isDistributorLocked: false,

      loading:             false,
      scheduleList:        [],

      ALL,
    };
  },

  computed: {
    isDistributorUser() {
      const u = String(this.userRole || '').toUpperCase().trim();
      return u === 'DISTRIBUTOR' || u.startsWith('DISTRIBUTOR') || u === 'DIS' || u.startsWith('DIS_');
    },
  },

  mounted() {
    this.fetchDistributors();
  },

  methods: {

    async fetchDistributors() {
      this.loading = true;
      try {
        const response = await fetchDistributors(this.profileId, this.token);
        const list = response?.data?.data || [];

        this.distributorsList    = [];
        this.selectedDistributor = '';
        this.isDistributorLocked = false;

        // Distributor user: auto-select and lock their own entry
        if (this.isDistributorUser && this.profileId) {
          const mine = list.find(d => Number(d.profile_id) === Number(this.profileId));
          if (mine) {
            this.distributorsList    = [mine];
            this.selectedDistributor = String(mine.profile_id);
            this.isDistributorLocked = true;
            return;
          }
        }

        this.distributorsList = list;
      } catch (error) {
        console.error('Error fetching distributors:', error);
        this.toast.error('Failed to load distributors.');
      } finally {
        this.loading = false;
      }
    },

    async performSearch() {
      if (!this.fromDate) {
        this.toast.warning('Please select a From Date.');
        return;
      }
      if (!this.toDate) {
        this.toast.warning('Please select a To Date.');
        return;
      }
      if (this.fromDate > this.toDate) {
        this.toast.warning('From Date cannot be later than To Date.');
        return;
      }
      if (!this.selectedDistributor) {
        this.toast.warning('Please select a distributor.');
        return;
      }

      this.loading      = true;
      this.scheduleList = [];

      const payload = {
    distributor_id: this.selectedDistributor === this.ALL
                        ? null                          // admin viewing all → no filter
                        : this.selectedDistributor      // specific distributor selected
                        || (this.isDistributorUser ? this.profileId : null), // fallback for distributor user
    from_date: this.fromDate,
    to_date:   this.toDate,
};
      console.log('Schedule search payload:', payload);

      try {
        const response = await fetchDeliveryScheduleList(payload, this.token);

        if (response.data.status === 'success') {
          this.scheduleList = response.data.data.map(item => ({
            id:              item.id,
            date_schedule:   item.date_schedule,
            delivery_window: item.delivery_window,
            routes:          item.routes  || [],  // array of route name strings from API
            dealers:         item.dealers || [],  // array of dealer name strings from API
          }));

          if (this.scheduleList.length === 0) {
            this.toast.info('No schedules found for the selected criteria.');
          }
        } else {
          this.toast.error(response.data.message || 'Failed to load schedules.');
        }
      } catch (error) {
        console.error('Error fetching schedule list:', error);
        this.toast.error('Something went wrong. Please try again.');
      } finally {
        this.loading = false;
      }
    },

  },
};
</script>

<style scoped lang="scss">
.form-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  width: 100%;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.search-input {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  width: 100%;

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
    outline: none;
  }

  &:hover {
    border-color: #0056b3;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 15px;
}

.new-dispatch-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 0;
  position: absolute;
  right: 20px;
  top: 20px;
}

.sales-rep-select {
  max-height: 200px;
  overflow-y: auto;
}

.text-center {
  text-align: center;
}

/* ── Delivery window badge ── */
.window-badge {
  display: inline-block;
  background: #eef2f9;
  color: #1e3a5f;
  border: 1px solid #c8d6ec;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

/* ── Tag lists ── */
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  i { font-size: 11px; }
}

.tag-route {
  background: #eef2f9;
  color: #1e3a5f;
  border: 1px solid #c8d6ec;
}

.tag-dealer {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #86efac;
}

.no-data {
  color: #9ca3af;
  font-size: 13px;
}

/* ── Table ── */
.table {
  width: 100%;
  border-collapse: collapse;

  th {
    font-size: 13px;
    font-weight: 700;
    color: #1b2a50;
    padding: 12px 14px;
    background: #f8f9fb;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
  }

  td {
    padding: 12px 14px;
    font-size: 13px;
    color: #374151;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: top;
  }

  tbody tr:hover {
    background: #fafbff;
  }
}

@media (max-width: 992px) {
  .form-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .new-dispatch-container {
    position: static;
    justify-content: flex-start;
  }
}

@media (max-width: 600px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>